import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, History, MapPin } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAppointments } from '../../services/appointmentApi';
import { useToast } from '../Toast';
import StatusBadge from '../StatusBadge';
import { WidgetGrid } from '../WidgetGrid';
import { WidgetSettingsModal } from '../WidgetSettingsModal';
import { DashboardCustomizeHeader } from '../DashboardCustomizeHeader';
import { useWidgetPreferences } from '../../hooks/useWidgetPreferences';
import { DEMO_CLIENT_APPOINTMENTS } from '../../data/demoDashboardData';

interface ClientAppointment {
  id: string;
  property_name: string;
  service_type: string;
  scheduled_date: string;
  status: string;
  duration?: number;
  actual_duration?: number;
}

const ClientDashboard: React.FC<{ isDemoMode?: boolean }> = ({ isDemoMode = false }) => {
  const { t, formatDate, formatTime } = useI18n();
  const { user } = useAuth();
  const { showError } = useToast();
  const dashboardRoleKey = 'client' as const;
  const {
    preferences,
    updateWidgetVisibility,
    updateWidgetOrder,
    getVisibleWidgets,
    flushSave,
    isLoading: prefsLoading,
    setLayout,
    resetToDefault,
  } = useWidgetPreferences(dashboardRoleKey);
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [upcomingAppointments, setUpcomingAppointments] = useState<ClientAppointment[]>([]);
  const [recentAppointments, setRecentAppointments] = useState<ClientAppointment[]>([]);
  const [totalServices, setTotalServices] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isDemoMode) {
      setUpcomingAppointments(DEMO_CLIENT_APPOINTMENTS.upcoming);
      setRecentAppointments(DEMO_CLIENT_APPOINTMENTS.recent);
      setTotalServices(DEMO_CLIENT_APPOINTMENTS.totalServices);
      setIsLoading(false);
      return;
    }
    if (user?.id) {
      loadClientDashboard();
    }
  }, [user?.id, isDemoMode]);

  const loadClientDashboard = async () => {
    setIsLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Buscar agendamentos do cliente
      let appointments: any = null;
      try {
        appointments = await getAppointments({
          client_id: user?.id,
        });
      } catch (error: any) {
        // Log detalhado para debug
        if (error.response) {
          console.warn('Erro ao carregar agendamentos do cliente:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
          });
        } else if (error.request) {
          console.warn('Erro de rede ao carregar agendamentos - servidor não respondeu');
        } else {
          console.warn('Erro ao carregar agendamentos:', error.message);
        }
        // Continuar com arrays vazios
        setUpcomingAppointments([]);
        setRecentAppointments([]);
        setTotalServices(0);
        setIsLoading(false);
        return;
      }

      if (!appointments) {
        setUpcomingAppointments([]);
        setRecentAppointments([]);
        setTotalServices(0);
        return;
      }

      const appointmentsArray = Array.isArray(appointments) 
        ? appointments 
        : (appointments?.appointments || appointments?.data || []);

      // Filtrar próximos agendamentos (hoje e futuro)
      const upcoming = appointmentsArray
        .filter((apt: any) => {
          const aptDate = new Date(apt.scheduled_date);
          return aptDate >= today && (apt.status !== 'completed' && apt.status !== 'concluído' && apt.status !== 'concluido' && apt.status !== 'cancelled' && apt.status !== 'cancelado');
        })
        .sort((a: any, b: any) => {
          const dateA = new Date(a.scheduled_date);
          const dateB = new Date(b.scheduled_date);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 5)
        .map((apt: any) => ({
          id: apt.id,
          property_name: apt.property_name || 'Propriedade não informada',
          service_type: apt.service_type || 'limpeza',
          scheduled_date: apt.scheduled_date,
          status: apt.status || 'agendado',
          duration: apt.duration || 120,
        }));

      // Filtrar histórico recente (últimos 10 completados)
      const recent = appointmentsArray
        .filter((apt: any) => {
          return apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido';
        })
        .sort((a: any, b: any) => {
          const dateA = new Date(a.scheduled_date || a.completed_at);
          const dateB = new Date(b.scheduled_date || b.completed_at);
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 10)
        .map((apt: any) => ({
          id: apt.id,
          property_name: apt.property_name || 'Propriedade não informada',
          service_type: apt.service_type || 'limpeza',
          scheduled_date: apt.scheduled_date || apt.completed_at,
          status: apt.status || 'completed',
          duration: apt.duration || 120,
          actual_duration: apt.actual_duration,
        }));

      setUpcomingAppointments(upcoming);
      setRecentAppointments(recent);
      setTotalServices(appointmentsArray.length);
    } catch (error: any) {
      // Log detalhado para debug
      console.error('Erro ao processar dados do dashboard:', error);
      // Não mostrar erro ao usuário se for apenas falta de dados
      if (error.message && !error.message.includes('agendamentos')) {
        showError('Erro ao carregar histórico de serviços');
      }
      // Garantir que os estados estão definidos mesmo em caso de erro
      setUpcomingAppointments([]);
      setRecentAppointments([]);
      setTotalServices(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      limpeza: 'Limpeza',
      deep_cleaning: 'Limpeza Profunda',
      manutencao: 'Manutenção',
      organizacao: 'Organização',
    };
    return labels[type] || type;
  };

  const availableWidgets = useMemo(
    () => [
      { id: 'upcomingAppointments', title: t('dashboardWidgetUpcoming'), description: '' },
      { id: 'recentAppointments', title: t('dashboardWidgetRecent'), description: '' },
      { id: 'totalServices', title: t('dashboardWidgetTotalServices'), description: '' },
    ],
    [t]
  );

  const widgets = useMemo(
    () => [
      {
        id: 'totalServices',
        title: t('dashboardWidgetTotalServices'),
        content: (
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>
            {isLoading ? '...' : totalServices}
          </div>
        ),
      },
      {
        id: 'upcomingAppointments',
        title: t('dashboardWidgetUpcoming'),
        content:
          upcomingAppointments.length === 0 ? (
            <div style={{ color: '#666' }}>{t('dashboardNoUpcoming')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {upcomingAppointments.map(appointment => {
                const appointmentDate = new Date(appointment.scheduled_date);
                return (
                  <div
                    key={appointment.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '10px',
                    }}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 8px 0' }}>{getServiceTypeLabel(appointment.service_type)}</h4>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {formatDate(appointmentDate)} {formatTime(appointmentDate)} · {appointment.property_name}
                      </div>
                    </div>
                    <StatusBadge status={appointment.status} size="medium" showIcon={true} />
                  </div>
                );
              })}
            </div>
          ),
      },
      {
        id: 'recentAppointments',
        title: t('dashboardWidgetRecent'),
        content: isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>{t('loading')}</div>
        ) : recentAppointments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666' }}>{t('dashboardNoRecentServices')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {recentAppointments.map(appointment => {
              const appointmentDate = new Date(appointment.scheduled_date);
              return (
                <div
                  key={appointment.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{getServiceTypeLabel(appointment.service_type)}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {formatDate(appointmentDate)} - {appointment.property_name}
                    </div>
                  </div>
                  <StatusBadge status={appointment.status} size="small" showIcon={true} />
                </div>
              );
            })}
          </div>
        ),
      },
    ],
    [isLoading, upcomingAppointments, recentAppointments, totalServices, t, formatDate, formatTime]
  );

  const handleToggleCustomize = async () => {
    if (isCustomizeMode) {
      try {
        await flushSave();
      } catch {
        showError(t('dashboardSaveError'));
      }
    }
    setIsCustomizeMode(prev => !prev);
  };

  return (
    <div style={{ padding: '30px' }}>
      <div
        style={{
          marginBottom: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('dashboardClientTitle')}</h1>
          <p style={{ color: '#666', margin: 0 }}>{t('dashboardClientSubtitle')}</p>
        </div>
        <DashboardCustomizeHeader
          isCustomizeMode={isCustomizeMode}
          onToggleCustomize={handleToggleCustomize}
          onOpenSettings={() => setShowWidgetSettings(true)}
        />
      </div>

      <WidgetGrid
        widgets={widgets}
        preferences={preferences}
        isLoading={prefsLoading}
        editable={isCustomizeMode}
        columns={2}
        onReorder={updateWidgetOrder}
        onToggleVisibility={updateWidgetVisibility}
        getVisibleWidgets={getVisibleWidgets}
      />

      <WidgetSettingsModal
        isOpen={showWidgetSettings}
        onClose={() => setShowWidgetSettings(false)}
        preferences={preferences}
        availableWidgets={availableWidgets}
        onToggleVisibility={updateWidgetVisibility}
        onSetLayout={setLayout}
        onReset={() => resetToDefault()}
      />
    </div>
  );
};

export default ClientDashboard;

